import React, { forwardRef, useState, useEffect } from 'react';
import { JWT_HOST_API } from "configs/auth.config";
import { normalizeToUtc, getLocalDateTime } from 'utils/dateUtils';
// import sensationCrm from "assets/images/Sensation-Logo-v2.svg";
// import PcteLogo from "assets/images/PcteLogo.svg";
import PcteLogoNew from "assets/images/PctelogoNewOne.png";

export const PaidInvoiceTemplate = forwardRef(({ invoiceData }, ref) => {
  const [companyLogo, setCompanyLogo] = useState(PcteLogoNew);
  const [logoError, setLogoError] = useState(false);

  useEffect(() => {
    const setCompanyLogoFromUrl = () => {
      try {
        const hostname = window.location.hostname;
        let folderName = hostname;
        if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
          folderName = 'localhost';
        }
        const apiHost = JWT_HOST_API.endsWith('/') ? JWT_HOST_API : `${JWT_HOST_API}/`;
        const logoUrl = `${apiHost}CompanyLogos/${folderName}/main.png`;

        setCompanyLogo(logoUrl);
      } catch (error) {
        console.error("Failed to set company logo from URL", error);
      }
    };
    setCompanyLogoFromUrl();
  }, []);

  if (!invoiceData) return null;

  const {
    FirstName,
    LastName,
    FullName,
    CollegeLogo,
    IsScholorship,
    PaymentDetails = []
  } = invoiceData;

  const detail = PaymentDetails[0] || {};
  const isScholApplied = detail.IsScolarShip || detail.IsScholorship || detail.IsScholarship || IsScholorship || invoiceData.IsScolarShip || invoiceData.IsScholarship;
  const payments = detail.Payments || [];
  const primaryPayment = payments[0] || {};
  const currencySymbol = (detail.Currency === 'INR') ? '₹' : '$';

  const collegeDisplayName = detail.CollegeName || invoiceData.College || 'University / College Name';
  const courseDisplayName = detail.CourseName || invoiceData.Course || 'N/A';

  const formatDateFormal = (dateString) => {
    if (!dateString) return 'N/A';
    const date = getLocalDateTime(dateString);
    if (!date) return 'N/A';
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <div
      ref={ref}
      className="bg-white p-12 max-w-[850px] mx-auto text-slate-800 font-sans border border-gray-100 shadow-sm"
      style={{ minHeight: '1000px' }}
    >
      <div className="flex flex-col items-center border-b-2 border-slate-900 pb-8 mb-10 text-center">
        <img
          src={companyLogo}
          alt="College Logo"
          className="h-16 w-auto object-contain mb-4"
          onError={(e) => {
            if (e.target.src !== PcteLogoNew) {
              e.target.src = PcteLogoNew;
            }
          }}
        />
        <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tight">{collegeDisplayName}</h1>
        <div className="mt-4 inline-block bg-slate-900 text-white px-6 py-1 text-sm font-bold uppercase tracking-widest rounded-full">
          Fee Receipt
        </div>
      </div>

      {/* Info Grid */}
      <div className="grid grid-cols-2 gap-10 mb-12">
        {/* Student Data */}
        <div className="space-y-4">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest border-b pb-2">Student Information</h3>
          <div className="space-y-2">
            <div className="flex text-sm">
              <span className="text-slate-500 w-32">Full Name:</span>
              <span className="font-bold text-slate-900 uppercase">{FullName || `${FirstName} ${LastName}`}</span>
            </div>
            <div className="flex text-sm">
              <span className="text-slate-500 w-32">Course:</span>
              <span className="font-bold text-slate-700 uppercase">{courseDisplayName}</span>
            </div>
            <div className="flex text-sm">
              <span className="text-slate-500 w-32">First Name:</span>
              <span className="font-semibold text-slate-700">{FirstName || 'N/A'}</span>
            </div>
            <div className="flex text-sm">
              <span className="text-slate-500 w-32">Last Name:</span>
              <span className="font-semibold text-slate-700">{LastName || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Transaction Data */}
        <div className="space-y-4">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest border-b pb-2">Transaction Details</h3>
          <div className="space-y-2">
            <div className="flex text-sm">
              <span className="text-slate-500 w-32">Date:</span>
              <span className="font-bold text-slate-900">{formatDateFormal(detail.PaymentDate || detail.CreatedDate)}</span>
            </div>
            {primaryPayment.PaymentTime && (
              <div className="flex text-sm">
                <span className="text-slate-500 w-32">Time:</span>
                <span className="font-semibold text-slate-700">{getLocalDateTime(primaryPayment.PaymentTime)?.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}</span>
              </div>
            )}
            <div className="flex text-sm">
              <span className="text-slate-500 w-32">Status:</span>
              <span className="font-bold text-emerald-600 uppercase">{primaryPayment.PaymentStatus || detail.LinkStatus || 'PAID'}</span>
            </div>
            <div className="flex text-sm">
              <span className="text-slate-500 w-32">Method:</span>
              <span className="font-semibold text-slate-700 uppercase">{primaryPayment.PaymentMethod || 'N/A'}</span>
            </div>
            <div className="flex text-sm">
              <span className="text-slate-500 w-32">Ref ID:</span>
              <span className="font-semibold text-slate-700">{primaryPayment.BankReference || 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Fee Table */}
      <div className="mb-12">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-50 border-y border-slate-200 uppercase text-[10px] font-bold tracking-wider">
              <th className="py-4 px-6">Description</th>
              <th className="py-4 px-6 text-right">Amount ({detail.Currency})</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {detail.CourseFee > 0 && (
              <tr className="border-b border-slate-100">
                <td className="py-5 px-6">
                  <p className="font-bold text-slate-800">Academic / Course Fee</p>
                </td>
                <td className="py-5 px-6 text-right font-bold text-slate-900">
                  {currencySymbol} {detail.CourseFee?.toLocaleString()}
                </td>
              </tr>
            )}
            {detail.HostelFee > 0 && (
              <tr className="border-b border-slate-100">
                <td className="py-5 px-6">
                  <p className="font-bold text-slate-800">Hostel Fee</p>
                </td>
                <td className="py-5 px-6 text-right font-bold text-slate-900">
                  {currencySymbol} {detail.HostelFee?.toLocaleString()}
                </td>
              </tr>
            )}
            {detail.PTUCounselingFee > 0 && (
              <tr className="border-b border-slate-100">
                <td className="py-5 px-6">
                  <p className="font-bold text-slate-800">PTU Counseling Fee</p>
                </td>
                <td className="py-5 px-6 text-right font-bold text-slate-900">
                  {currencySymbol} {detail.PTUCounselingFee?.toLocaleString()}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Total Section */}
      <div className="flex justify-between items-center border-t-2 border-slate-900 mt-4">
        <div>
          {(detail.Currency && detail.Currency !== 'INR') && (
            <div className={`px-3 py-1.5 text-[10px] font-bold rounded-md border flex items-center shadow-sm ${isScholApplied
              ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
              : 'bg-slate-50 text-slate-500 border-slate-200'
              }`}>
              <div className={`w-1.5 h-1.5 rounded-full mr-2 ${isScholApplied ? 'bg-emerald-500' : 'bg-slate-400'
                }`}></div>
              IsScholorshipApplied : {isScholApplied ? 'Yes' : 'No'}
            </div>
          )}
        </div>
        <div className="w-72 py-4 px-2">
          <div className="flex justify-between items-center text-slate-900">
            <span className="font-black uppercase text-xs tracking-widest">Total Amount</span>
            <span className="font-black text-2xl">{currencySymbol} {detail.Amount?.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-auto pt-20 text-center border-t border-slate-100">
        <p className="text-[10px] text-slate-400 uppercase tracking-widest">Computerd-generated receipt. No signature required.</p>
        <p className="text-[10px] text-slate-400 mt-2 font-bold">{detail.Description || primaryPayment.PaymentMessage || 'Payment processed successfully'}</p>
        {primaryPayment.PaymentTime && (
          <p className="text-[9px] text-slate-400 mt-1 italic">Payment Date/Time: {getLocalDateTime(primaryPayment.PaymentTime)?.toLocaleString('en-IN')}</p>
        )}
      </div>
    </div>
  );
});

PaidInvoiceTemplate.displayName = 'PaidInvoiceTemplate';
